import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';
import ContactForm from '../components/ContactForm';
import ReviewsSlider from '../components/ReviewsSlider';

export default function Prices() {
  const { getPage } = useCms();
  const pageData = getPage('priser') || getPage('hvad-koster-laserrens');

  const heroTitle = (typeof pageData?.title === 'object' ? pageData?.title?.da : pageData?.title) || 'Hvad koster laserrens?';
  const heroImage = Array.isArray(pageData?.images) && pageData.images.length > 0 
    ? pageData.images[0] 
    : 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/imgi-94-billede-med-blur-scaled.jpg';
  const trialImage = Array.isArray(pageData?.images) && pageData.images.length > 1
    ? pageData.images[1]
    : 'https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/proevespor-laserrens-granittrappe-650x650.jpg';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Hvad koster laserrens? Priser & Faktorer | ECOLASER</title>
        <meta name="description" content="Få overblik over priser på laserrensning. Vi arbejder ud fra en kvadratmeterpris baseret på overfladens karakter, alder og geometri. Få et tilbud her." />
        <meta property="og:title" content="Hvad koster laserrens? Priser & Faktorer | ECOLASER" />
        <meta property="og:description" content="Få overblik over priser på laserrensning. Vi arbejder ud fra en kvadratmeterpris baseret på overfladens karakter, alder og geometri. Få et tilbud her." />
        <meta property="og:image" content={heroImage} />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage.includes('assets.auxodev.dk') && !heroImage.includes('/cdn-cgi/image/') ? heroImage.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/') : heroImage}
            alt="Ecolaser priser baggrund"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-3">
          <div className="flex flex-wrap items-center gap-2.5 mb-2">
            <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/30 px-4 py-1.5 rounded-full text-brand-green text-xs font-semibold uppercase tracking-wider">
              <i className="fa-solid fa-tag" /> Gennemsigtig prissætning
            </div>

            {/* Diskret reminder badge / knap om prøverensning */}
            <div 
              role="status"
              aria-label="Book en prøverensning og oplev forskellen"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 backdrop-blur-sm px-4 py-1.5 rounded-full text-zinc-100 text-xs font-medium tracking-wide shadow-sm cursor-default select-none transition-all duration-200"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-green opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-green" />
              </span>
              <i className="fa-solid fa-wand-magic-sparkles text-brand-green text-xs" />
              <span>Book en prøverensning og oplev forskellen</span>
            </div>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">{heroTitle}</h1>
          <p className="text-zinc-200 text-sm md:text-base max-w-2xl leading-relaxed">
            Hos ECOLASER arbejder vi som udgangspunkt ud fra en kvadratmeterpris. Da ingen opgaver er ens, varierer prisen alt efter overfladens karakter.
          </p>
        </div>
      </section>

      {/* Main Pricing Explanation & Form */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column: First text section */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-block bg-brand-green/10 text-brand-green px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider">
                Prisstruktur
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-zinc-900 leading-tight">
                Individuel vurdering sikrer den rette pris
              </h2>
              <div className="h-1 w-12 bg-brand-green rounded-full" />

              <div className="space-y-4 text-zinc-600 text-sm md:text-base leading-relaxed">
                <p>
                  Hos <strong>ECOLASER</strong> arbejder vi som udgangspunkt ud fra en kvadratmeterpris. Da ingen opgaver er ens, varierer prisen alt efter overfladens karakter – lige fra de store, plane facadepartier til bygningens fine, arkitektoniske detaljer.
                </p>
                <p>
                  Derfor kan vi ikke give en fast standardpris på forhånd. Vores tidsforbrug og specifikke tilgang afhænger nemlig særligt af <strong>fire faktorer</strong>, der hver især kræver specifikke hensyn.
                </p>
                <p>
                  <a 
                    href="#fire-faktorer" 
                    className="inline-flex items-center gap-1.5 text-brand-green hover:text-emerald-700 font-medium hover:underline group transition-colors"
                  >
                    <span>Læs mere om de fire faktorer, der afgør prisen herunder.</span>
                    <i className="fa-solid fa-arrow-down text-xs group-hover:translate-y-0.5 transition-transform" />
                  </a>
                </p>
              </div>

              {/* Reassurance points */}
              <div className="pt-4 space-y-3.5 border-t border-zinc-100">
                <div className="flex items-start gap-3 text-sm text-zinc-700">
                  <div className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 text-xs mt-0.5">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span><strong>100% uforpligtende tilbud</strong> tilpasset dit konkrete projekt</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-zinc-700">
                  <div className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 text-xs mt-0.5">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span><strong>Mulighed for prøverensning</strong>, så du ser resultatet forinden</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-zinc-700">
                  <div className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 text-xs mt-0.5">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span><strong>Hurtig tilbagemelding</strong> – vi vender hurtigt retur på din forespørgsel</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-zinc-700">
                  <div className="w-5 h-5 rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center shrink-0 text-xs mt-0.5">
                    <i className="fa-solid fa-check" />
                  </div>
                  <span><strong>Landsdækkende kørsel</strong> i Jylland, på Fyn og Sjælland</span>
                </div>
              </div>

              {/* Direct call box */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-5 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-brand-green text-white flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <div className="text-xs text-zinc-500 font-medium">Vil du hellere drøfte opgaven over telefonen?</div>
                  <a href="tel:29100810" className="text-sm font-bold text-zinc-900 hover:text-brand-green transition-colors">
                    Ring direkte på +45 29 10 08 10
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-7" id="indhent-pris">
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-6">
                <div className="space-y-2">
                  <h2 className="text-2xl md:text-3xl font-bold text-zinc-900">Indhent en pris</h2>
                  <p className="text-zinc-600 text-sm">
                    Udfyld formularen herunder for at indhente et gratis og uforpligtende tilbud. Beskriv gerne din opgave og omfanget så detaljeret som muligt.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>

          {/* The 4 Factors List */}
          <div id="fire-faktorer" className="mt-20 pt-16 border-t border-zinc-200 scroll-mt-24">
            <div className="max-w-3xl mb-12">
              <div className="inline-block bg-brand-green/10 text-brand-green px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider mb-3">
                Nøglefaktorer
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-zinc-900">
                De fire faktorer, der afgør prisen
              </h3>
              <p className="text-zinc-600 text-sm md:text-base mt-2 leading-relaxed">
                Vores tidsforbrug og specifikke tilgang afhænger særligt af fire faktorer, der hver især kræver specifikke hensyn:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Factor 1 */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center text-xl shrink-0">
                  <i className="fa-regular fa-landmark" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-zinc-900">
                    Bygningens alder og beskaffenhed
                  </h4>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                    En historisk facade kræver en yderst forsigtig og tidskrævende tilgang for ikke at lide uoprettelig skade.
                  </p>
                </div>
              </div>

              {/* Factor 2 */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center text-xl shrink-0">
                  <i className="fa-regular fa-layer-group" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-zinc-900">
                    Materialets type og tilstand
                  </h4>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                    Forskellen på alt fra gamle, porøse sandsten til mursten med solid brandhud betyder, at overfladens tolerance over for påvirkning varierer markant. Hvert materiale kræver derfor sin helt egen, unikke indstilling for at blive beskyttet optimalt.
                  </p>
                </div>
              </div>

              {/* Factor 3 */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center text-xl shrink-0">
                  <i className="fa-regular fa-compass-drafting" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-zinc-900">
                    Geometri og detaljer
                  </h4>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                    Komplekse ornamenter, udskæringer og gesimser kræver kirurgisk præcision og tålmodighed for at bevare de oprindelige detaljer, mens store, plane flader tillader et hurtigere arbejdstempo.
                  </p>
                </div>
              </div>

              {/* Factor 4 */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-12 h-12 rounded-xl bg-brand-green/10 text-brand-green flex items-center justify-center text-xl shrink-0">
                  <i className="fa-regular fa-smog" />
                </div>
                <div className="space-y-2">
                  <h4 className="text-lg font-bold text-zinc-900">
                    Belægningens type
                  </h4>
                  <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                    Både lagets tykkelse og dybde spiller ind, ligesom det kræver særlig agtpågivenhed at opløse sod og urbant smuds, der har siddet fast igennem årtier, uden at beskadige det underliggende materiale.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Cleaning Section */}
      <section className="bg-zinc-50 py-20 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Image of Trial Cleaning */}
            <div className="lg:col-span-5 relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-zinc-200 shadow-md">
                <img
                  src={trialImage.includes('assets.auxodev.dk') && !trialImage.includes('/cdn-cgi/image/') ? trialImage.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/') : trialImage}
                  alt="Prøverensning med laser på granittrappe"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-zinc-950 text-white p-4 rounded-xl border border-zinc-800 shadow-lg max-w-[220px] hidden sm:block">
                <span className="text-xs text-brand-green font-bold uppercase tracking-wider block mb-1">Prøvespor</span>
                <p className="text-[11px] text-zinc-300 leading-relaxed">
                  Tydelig forskel på den rensede overflade og det snavsede område på en granittrappe.
                </p>
              </div>
            </div>

            {/* Right Column: Text */}
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-block bg-brand-green/10 text-brand-green px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider">
                100% Tryghed
              </div>
              <h2 className="text-2xl md:text-4xl font-bold text-zinc-900 leading-tight">
                Få fuld tryghed med en prøverensning
              </h2>
              <p className="text-zinc-600 text-sm md:text-base leading-relaxed">
                For at give dig den helt rigtige pris, foretager vi altid en besigtigelse eller en prøverensning først. Det er din garanti for et retvisende tilbud – helt uden ubehagelige overraskelser.
              </p>
              <div className="pt-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-light text-white font-bold px-6 py-3.5 rounded-lg text-xs uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_15px_rgba(118,184,42,0.3)]"
                >
                  Få et uforpligtende tilbud <i className="fa-solid fa-arrow-right" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Kundeanmeldelser (Reviews Slider) */}
      <section className="bg-white py-24 border-t border-zinc-200">
        <div className="text-center flex flex-col items-center gap-4 mb-12">
          <div className="inline-block bg-brand-green/10 text-brand-green px-3 py-1 rounded-md text-xs font-bold uppercase tracking-wider">
            Kundeudtalelser
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-zinc-900">
            Hvad siger vores kunder?
          </h2>
        </div>

        <ReviewsSlider />
      </section>
    </>
  );
}
